import { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PageLoader } from './components/PageLoader';
import DynamicPage from './components/DynamicPage';
import { moduleRoutes } from './modules/registry';
import { useModuleEnabled } from './hooks/usePublicConfig';
import { initAnalytics, trackPageView } from './lib/analytics';

// Core 404 (not a module) — lazy so it costs nothing on the happy path.
const NotFound = lazy(() => import('./pages/NotFound'));
const ModuleUnavailable = lazy(() => import('./components/ModuleUnavailable'));
// /admin → WordPress login when the platform admin module is not installed.
const AdminRedirect = lazy(() => import('./components/AdminRedirect'));
// freshSTART event pages (PRP-13, D2): declared before the events module's identical
// paths, so these win — the module still installs the signup relay and its bundle.
const GetInvolved = lazy(() => import('./pages/GetInvolved'));
const EventDetailRoute = lazy(() => import('./pages/EventDetailRoute'));

/**
 * Scroll on every link click (each navigation has a new location.key, so a link to the
 * current page counts too): to the `#hash` target when there is one, else to the top.
 * Pages are lazy and fill from WordPress, so a hash target may appear late: retry for
 * up to ~3s. Back/forward (POP) is left to the browser.
 */
function useScrollOnNavigate() {
  const { key, pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === 'POP' && !hash) return;
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    const find = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: 'start' });
      return el !== null || ++tries > 30;
    };
    if (find()) return;
    const timer = window.setInterval(() => find() && window.clearInterval(timer), 100);
    return () => window.clearInterval(timer);
    // `key` changes on every navigation, including a link to the page already shown.
  }, [key, pathname, hash, navType]);
}

/** Tier-2 runtime gate for public module routes. Optimistic while config loads;
 *  the admin module is never gated (lockout prevention). */
function RuntimeModuleGate({ moduleId, children }: { moduleId: string; children: JSX.Element }) {
  const enabled = useModuleEnabled(moduleId);
  if (!enabled) return <ModuleUnavailable />;
  return children;
}

/**
 * Main Application Component
 *
 * Provides the core application structure with routing, header, and footer.
 * Core routes are '/' and '/:slug' (the WordPress page engine); every other
 * route is composed from the module registry (see src/modules/README.md).
 *
 * @returns {JSX.Element} The main app component with routing configuration
 */
export const App = () => {
  // Application initialization state
  const [isLoading, setIsLoading] = useState(true);
  const { pathname } = useLocation();
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');

  useScrollOnNavigate();

  // Page-enter fade on route changes only: the first route renders without it so the
  // hero (the LCP) is never faded in.
  const firstPath = useRef(pathname);
  const pageEnter = pathname !== firstPath.current ? 'page-enter' : '';

  useEffect(() => {
    setIsLoading(false);
    initAnalytics();
  }, []);

  // GA4 SPA page views (no-op unless VITE_GA4_ID is set). Deferred a tick so
  // Helmet has applied the new route's <title>.
  useEffect(() => {
    const t = setTimeout(() => trackPageView(pathname), 0);
    return () => clearTimeout(t);
  }, [pathname]);

  // Show loading spinner during app initialization
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-container">
      {/* Site header with navigation (hidden on the admin dashboard) */}
      {!isAdminRoute && <Header />}

      {/* Main content area with routing */}
      <main className="flex-grow" data-testid="main-content">
        <Suspense fallback={<PageLoader />}>
          {/* Keyed by pathname (not hash) so hash links keep the page mounted. flow-root
              keeps the heroes' -mt-20 inside this box: without it the margin collapses
              through and the whole box shifts up when a lazy page mounts (CLS). */}
          <div key={pathname} className={`flow-root ${pageEnter}`}>
            <Routes>
              {/* Core: WordPress page engine */}
              <Route path="/" element={<DynamicPage />} />

              {/* freshSTART: Get Involved index + event detail wrapper (SEO, JSON-LD).
                  Equal-score routes match in declaration order, so these beat the
                  events module's /events and /events/:slug below. */}
              <Route path="/events" element={<GetInvolved />} />
              <Route path="/events/:slug" element={<EventDetailRoute />} />

              {/* Module routes — rendered BEFORE the /:slug WP catch-all (which
                  would otherwise swallow single-segment paths like /admin).
                  v6 ranking also guarantees static segments outrank /:slug. */}
              {moduleRoutes.map(({ path, moduleId, Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <RuntimeModuleGate moduleId={moduleId}>
                      <Component />
                    </RuntimeModuleGate>
                  }
                />
              ))}

              {/* No platform admin installed: /admin hands off to the WordPress login */}
              {!__MODULE_ADMIN__ && <Route path="/admin/*" element={<AdminRedirect />} />}

              {/* Core: WP catch-all for single-segment slugs */}
              <Route path="/:slug" element={<DynamicPage />} />

              {/* Multi-segment unknowns (/:slug can't match these) */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </Suspense>
      </main>

      {/* Site footer (hidden on the admin dashboard) */}
      {!isAdminRoute && <Footer />}
    </div>
  );
};
